<template>
  <div class="card" :class="wrapperClass">
    <div class="card-body py-2 px-3 small">
      <!-- Plain-language summary -->
      <section v-if="displayHeadline" class="plain-language-summary mb-3 p-3 rounded" :class="summaryBoxClass">
        <h5 class="mb-2 font-weight-bold">{{ displayHeadline }}</h5>
        <p class="mb-2">{{ plainLanguage.summary }}</p>

        <ol class="pipeline-steps pl-3 mb-2">
          <li
            v-for="step in plainLanguage.steps"
            :key="step.key"
            class="mb-2"
          >
            <span :class="stepStatusClass(step.status)" class="mr-1 font-weight-bold">
              {{ stepStatusIcon(step.status) }}
            </span>
            <strong>{{ step.title }}</strong>
            <span class="d-block text-muted mt-1">{{ step.detail }}</span>
          </li>
        </ol>

        <section
          v-if="showSalesridgeResponseBox"
          class="salesridge-response-summary mb-3 p-2 rounded border bg-white"
        >
          <h6 class="mb-2 font-weight-bold text-uppercase small text-muted">
            SalesRidge response
          </h6>
          <p v-if="salesridgeResponse.stored_note" class="small text-muted mb-2">
            {{ salesridgeResponse.stored_note }}
            Live stats are fetched from SalesRidge when you open this panel.
          </p>

          <dl class="row mb-0 small">
            <dt class="col-sm-5 mb-0">SalesRidge order id</dt>
            <dd class="col-sm-7 mb-1">
              <code v-if="salesridgeResponse.salesridge_id">{{ salesridgeResponse.salesridge_id }}</code>
              <span v-else class="text-muted">—</span>
            </dd>

            <template v-if="hasStoredLeadsTotal">
              <dt class="col-sm-5 mb-0">At launch (BD)</dt>
              <dd class="col-sm-7 mb-1">
                leadsTotal = {{ salesridgeResponse.stored_leads_total | numberFormat }}
                <span
                  v-if="salesridgeResponse.stored_apollo_total != null"
                  class="text-muted"
                >
                  · apollo_total_contacts =
                  {{ salesridgeResponse.stored_apollo_total | numberFormat }}
                </span>
              </dd>
            </template>

            <template v-if="salesridgeResponse.has_live_order_stats">
              <dt class="col-sm-5 mb-0">Live getOrderStats</dt>
              <dd class="col-sm-7 mb-1">
                uniqueLeads =
                {{ salesridgeLiveStat('uniqueLeads') | numberFormat }}
                · batches =
                {{ salesridgeLiveStat('batches') | numberFormat }}
                · exported =
                {{ salesridgeLiveStat('exported') | numberFormat }}
                · ready =
                {{ salesridgeLiveStat('ready') | numberFormat }}
                <span
                  v-if="salesridgeResponse.live_fetched_at"
                  class="d-block text-muted"
                >
                  checked {{ salesridgeResponse.live_fetched_at }}
                </span>
              </dd>
            </template>

            <template v-if="salesridgeExportTaskStatus">
              <dt class="col-sm-5 mb-0">Export task</dt>
              <dd class="col-sm-7 mb-1">
                {{ salesridgeExportTaskStatus }}
                <span v-if="salesridgeExportTaskMessage" class="d-block">
                  {{ salesridgeExportTaskMessage }}
                </span>
                <span
                  v-if="salesridgeBatchStatusSummary"
                  class="d-block text-muted"
                >
                  {{ salesridgeBatchStatusSummary }}
                </span>
              </dd>
            </template>

            <template v-if="salesridgeResponse.local_batch_count != null">
              <dt class="col-sm-5 mb-0">Oliverlist import</dt>
              <dd class="col-sm-7 mb-1">
                {{ salesridgeResponse.local_batch_count | numberFormat }} batch(es) stored
                <span v-if="salesridgeResponse.local_pending_batch_count > 0">
                  · {{ salesridgeResponse.local_pending_batch_count | numberFormat }} pending
                </span>
              </dd>
            </template>

            <template v-if="salesridgeLaunchErrorMessage">
              <dt class="col-sm-5 mb-0">Launch error (BD)</dt>
              <dd class="col-sm-7 mb-1 text-danger">
                {{ salesridgeLaunchErrorMessage }}
              </dd>
            </template>
          </dl>

          <p
            v-if="salesridgeLiveFetchErrors.length"
            class="small text-danger mb-2"
          >
            Could not read live SalesRidge:
            {{ salesridgeLiveFetchErrors.join(', ') }}
          </p>

          <details v-if="salesridgeRawJson" class="small mt-1">
            <summary class="cursor-pointer text-muted">Raw API payload</summary>
            <pre class="salesridge-raw-json mb-0 mt-2 p-2 bg-light rounded">{{ salesridgeRawJson }}</pre>
          </details>
        </section>

        <p
          v-if="(plainLanguage.emails_sent_today || 0) > 0"
          class="mb-2 font-weight-bold"
        >
          {{ plainLanguage.emails_sent_today | numberFormat }} email(s) sent today
          <span v-if="plainLanguage.emails_sent_total" class="text-muted font-weight-normal">
            ({{ plainLanguage.emails_sent_total | numberFormat }} total on this order)
          </span>
        </p>
        <p v-if="plainLanguage.what_it_means" class="mb-2">
          {{ plainLanguage.what_it_means }}
        </p>
        <p v-if="plainLanguage.likely_fix" class="mb-2">
          <strong>What to do:</strong>
          {{ plainLanguage.likely_fix }}
        </p>
        <div v-if="canRelaunchSalesridge" class="mt-2">
          <button
            type="button"
            class="btn btn-sm btn-warning"
            :disabled="relaunching"
            @click="relaunchSalesridge"
          >
            <span v-if="relaunching" class="spinner-border spinner-border-sm mr-1" />
            {{ relaunchButtonLabel }}
          </button>
          <p v-if="relaunchMessage" class="small mb-0 mt-2" :class="relaunchMessageClass">
            {{ relaunchMessage }}
          </p>
          <p class="small text-muted mb-0 mt-1">
            Calls SalesRidge <code>leadsSearch</code> (launch only). Batches and contact
            import are handled by the usual crons afterward.
          </p>
        </div>
      </section>

      <!-- Technical details (collapsed by default) -->
      <details class="technical-details">
        <summary class="mb-2 font-weight-bold cursor-pointer text-muted">
          Technical details (support / debugging)
        </summary>

        <div v-if="alerts.length" class="mb-3">
          <h6 class="mb-2 text-uppercase text-muted">System notes</h6>
          <div
            v-for="(a, idx) in alerts"
            :key="'alert-' + idx"
            class="py-2 px-2 mb-1 rounded"
            :class="alertClass(a.tone)"
          >
            {{ a.message }}
          </div>
        </div>

        <h6 class="mb-2 text-uppercase text-muted">SalesRidge</h6>
        <div class="mb-2">
          <span :class="launchStatusBadgeClass">{{ launchStatusLabel }}</span>
        </div>
        <p v-if="health.salesridge_launch" class="mb-2 text-muted">
          {{ health.salesridge_launch.launch_status_label }}
        </p>
        <ul
          v-if="blockedReasons.length"
          class="alert alert-warning py-2 px-3 mb-3"
        >
          <li v-for="(reason, idx) in blockedReasons" :key="'br-' + idx" class="mb-0">
            {{ reason }}
          </li>
        </ul>
        <ul
          v-if="health.salesridge_launch && health.salesridge_launch.conditions.length"
          class="list-unstyled mb-3 pl-0"
        >
          <li
            v-for="c in health.salesridge_launch.conditions"
            :key="c.key"
            class="mb-1"
          >
            <span :class="conditionIconClass(c)">
              {{ conditionIcon(c) }}
            </span>
            {{ c.label }}
            <span
              v-if="c.actual !== undefined && c.actual !== null && c.actual !== ''"
              class="text-muted"
            >
              — {{ c.actual }}
            </span>
          </li>
        </ul>
        <dl v-if="health.salesridge_launch" class="row mb-3 small">
          <dt class="col-sm-4 mb-0">Search launched</dt>
          <dd class="col-sm-8 mb-1">
            <template v-if="health.salesridge_launch.salesridge_launched_at_label">
              {{ health.salesridge_launch.salesridge_launched_at_label }}
              <span
                v-if="health.salesridge_launch.salesridge_launched_ago"
                class="text-muted"
              >
                · {{ health.salesridge_launch.salesridge_launched_ago }}
              </span>
            </template>
            <span v-else>{{ health.salesridge_launch.salesridge || '—' }}</span>
          </dd>
          <dt class="col-sm-4 mb-0">salesridge_id</dt>
          <dd class="col-sm-8 mb-1">{{ health.salesridge_launch.salesridge_id || '—' }}</dd>
          <dt class="col-sm-4 mb-0">target total_contacts</dt>
          <dd class="col-sm-8 mb-1">
            {{
              health.salesridge_launch.target_total_contacts === null
                ? 'NULL'
                : (health.salesridge_launch.target_total_contacts | numberFormat)
            }}
          </dd>
          <dt class="col-sm-4 mb-0">Import batches</dt>
          <dd class="col-sm-8 mb-1">
            {{ health.salesridge_launch.salesridge_batch_count | numberFormat }}
            <span class="text-muted"> (Oliverlist DB)</span>
            <span
              v-if="health.salesridge_launch.last_batch_created"
              class="text-muted"
            >
              · last {{ health.salesridge_launch.last_batch_created }}
            </span>
          </dd>
        </dl>

        <div
          v-if="salesridgeRemote"
          class="alert py-2 px-3 mb-3"
          :class="salesridgeRemoteAlertClass"
        >
          <h6 class="mb-2 text-uppercase">SalesRidge live status</h6>
          <p v-if="health.salesridge_launch.remote_diagnosis" class="mb-2 font-weight-bold">
            {{ health.salesridge_launch.remote_diagnosis }}
          </p>
          <dl class="row mb-0 small">
            <dt class="col-sm-5 mb-0">SR unique leads</dt>
            <dd class="col-sm-7 mb-1">
              {{ salesridgeOrderStat('uniqueLeads') | numberFormat }}
            </dd>
            <dt class="col-sm-5 mb-0">SR batches / exported / ready</dt>
            <dd class="col-sm-7 mb-1">
              {{ salesridgeOrderStat('batches') | numberFormat }} /
              {{ salesridgeOrderStat('exported') | numberFormat }} /
              {{ salesridgeOrderStat('ready') | numberFormat }}
            </dd>
            <dt class="col-sm-5 mb-0">SR task</dt>
            <dd class="col-sm-7 mb-1">
              <span v-if="salesridgeTaskStatus">
                {{ salesridgeTaskStatus }}
                <span v-if="salesridgeTaskSource" class="text-muted">
                  ({{ salesridgeTaskSource }})
                </span>
              </span>
              <span v-else class="text-muted">—</span>
            </dd>
            <dt v-if="salesridgeTaskMessage" class="col-sm-5 mb-0">SR message</dt>
            <dd v-if="salesridgeTaskMessage" class="col-sm-7 mb-1">
              {{ salesridgeTaskMessage }}
            </dd>
            <dt class="col-sm-5 mb-0">API checked</dt>
            <dd class="col-sm-7 mb-0">
              {{ salesridgeRemote.fetched_at || '—' }}
            </dd>
          </dl>
        </div>

        <h6 class="mb-2 text-uppercase text-muted">Contact funnel</h6>
        <p v-if="contactFunnel.summary" class="mb-2 font-weight-bold">
          {{ contactFunnel.summary }}
        </p>
        <dl v-if="contactFunnel.bct_total !== undefined" class="row mb-3 small">
          <dt class="col-sm-5 mb-0">Contacts in order</dt>
          <dd class="col-sm-7 mb-1">{{ contactFunnel.bct_total | numberFormat }}</dd>
          <dt class="col-sm-5 mb-0">Usable for outreach</dt>
          <dd class="col-sm-7 mb-1">{{ contactFunnel.bct_valid | numberFormat }}</dd>
          <dt class="col-sm-5 mb-0">Invalid / blocked</dt>
          <dd class="col-sm-7 mb-1">{{ contactFunnel.bct_invalid | numberFormat }}</dd>
          <dt class="col-sm-5 mb-0">Imported last 24h</dt>
          <dd class="col-sm-7 mb-1">{{ contactFunnel.bct_last_24h | numberFormat }}</dd>
          <dt class="col-sm-5 mb-0">Assigned to campaigns</dt>
          <dd class="col-sm-7 mb-1">{{ contactFunnel.cbc_valid | numberFormat }}</dd>
          <dt class="col-sm-5 mb-0">Waiting for campaign</dt>
          <dd class="col-sm-7 mb-1 text-warning">
            {{ contactFunnel.not_assigned_to_campaign | numberFormat }}
          </dd>
        </dl>

        <h6 class="mb-2 text-uppercase text-muted">Sending stats</h6>
        <dl v-if="targetStats" class="row mb-3 small">
          <dt class="col-sm-5 mb-0">Sent today (campaigns)</dt>
          <dd class="col-sm-7 mb-1">
            {{ (plainLanguage.emails_sent_today || 0) | numberFormat }}
          </dd>
          <dt class="col-sm-5 mb-0">Sent total (order)</dt>
          <dd class="col-sm-7 mb-1">
            {{ (targetStats.total_email_sent_live != null ? targetStats.total_email_sent_live : 0) | numberFormat }}
          </dd>
          <dt class="col-sm-5 mb-0">Import Error counter</dt>
          <dd class="col-sm-7 mb-1 text-muted">
            {{ targetStats.total_email_sent | numberFormat }}
            <span class="d-block small">daily snapshot — can lag behind</span>
          </dd>
          <dt class="col-sm-5 mb-0">Contacts available to schedule</dt>
          <dd class="col-sm-7 mb-1">
            {{
              targetStats.available_contacts === null
                ? 'NULL'
                : (targetStats.available_contacts | numberFormat)
            }}
          </dd>
          <dt class="col-sm-5 mb-0">Contacts already reached</dt>
          <dd class="col-sm-7 mb-1">
            {{ targetStats.total_contacted_contacts | numberFormat }}
          </dd>
          <dt class="col-sm-5 mb-0">On Import Error page</dt>
          <dd class="col-sm-7 mb-1">
            <span
              :class="targetStats.matches_import_error ? 'badge badge-warning' : 'badge badge-light'"
            >
              {{ targetStats.matches_import_error ? 'Yes' : 'No' }}
            </span>
          </dd>
        </dl>

        <h6 class="mb-2 text-uppercase text-muted">Campaign send pipeline</h6>
        <p v-if="campaignPipelineCaption" class="small text-muted mb-2">
          {{ campaignPipelineCaption }}
        </p>
        <div class="table-responsive">
          <table
            v-if="campaignPipelineRows.length"
            class="table table-sm table-bordered mb-0 campaign-pipeline-table"
          >
            <thead>
              <tr>
                <th rowspan="2">Campaign</th>
                <th rowspan="2">Status</th>
                <th colspan="2" class="text-center border-bottom-0">Contacts</th>
                <th colspan="3" class="text-center border-bottom-0">Planner today</th>
                <th rowspan="2" class="text-right" title="csbc scheduled_date_key &gt; today">
                  After today
                </th>
                <th colspan="2" class="text-center border-bottom-0">Today sends</th>
                <th colspan="2" class="text-center border-bottom-0">Setup</th>
              </tr>
              <tr>
                <th class="text-right">In camp.</th>
                <th class="text-right">Avail.</th>
                <th class="text-right">Quota</th>
                <th>COI</th>
                <th>Owarm</th>
                <th class="text-right" title="Scheduled, not sent">Waiting</th>
                <th class="text-right" title="Marked sent today">Sent</th>
                <th>Spark</th>
                <th>Demo</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in campaignPipelineRows" :key="row.campaign_id">
                <td :title="row.campaign_name">{{ row.campaign_id }}</td>
                <td>{{ row.campaign_status_id }}</td>
                <td class="text-right">{{ row.contact_count | numberFormat }}</td>
                <td class="text-right">{{ row.available_contacts | numberFormat }}</td>
                <td class="text-right">
                  <template v-if="row.has_coi_today">
                    {{ row.quota_used }}/{{ row.quota_per_campaign }}
                    <span v-if="row.quota_full" class="badge badge-secondary ml-1">full</span>
                  </template>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>
                  <template v-if="row.has_coi_today">
                    {{ row.coi_status }} / c{{ row.coi_current }}
                  </template>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>
                  <template v-if="row.has_coi_today">
                    {{ row.owarm_paused ? 'paused' : 'ok' }}
                  </template>
                  <span v-else class="text-muted">—</span>
                </td>
                <td class="text-right">
                  {{ row.scheduled_after_today | numberFormat }}
                </td>
                <td class="text-right">{{ row.csbc_waiting_today | numberFormat }}</td>
                <td class="text-right">{{ row.csbc_sent_today | numberFormat }}</td>
                <td>
                  <span :class="row.activated_spark === 1 ? 'text-success' : 'text-danger'">
                    {{ row.activated_spark === 1 ? '✓' : '✗' }}
                  </span>
                </td>
                <td>
                  <span :class="row.demo_url_set ? 'text-success' : 'text-danger'">
                    {{ row.demo_url_set ? '✓' : '✗' }}
                  </span>
                </td>
              </tr>
            </tbody>
            <tfoot v-if="campaignPipelineTotals">
              <tr class="font-weight-bold small">
                <td colspan="7">Total ({{ campaignPipelineTotals.campaign_count }} campaigns)</td>
                <td class="text-right">
                  {{ campaignPipelineTotals.scheduled_after_today | numberFormat }}
                </td>
                <td class="text-right">
                  {{ campaignPipelineTotals.csbc_waiting_today | numberFormat }}
                </td>
                <td class="text-right">
                  {{ campaignPipelineTotals.csbc_sent_today | numberFormat }}
                </td>
                <td colspan="2" />
              </tr>
            </tfoot>
          </table>
          <p v-else class="mb-0 text-muted">No campaigns linked to this target.</p>
        </div>
      </details>
    </div>
  </div>
</template>

<script>
import { targetsService } from "@/_services";
import {
  formatSalesridgeRelaunchError,
  salesridgeLaunchButtonLabel,
  shouldShowSalesridgeLaunchButton,
} from "@/_helpers/salesridgeLaunchUi";

const LAUNCH_STATUS_LABELS = {
  pending_launch: "Pending launch (cron)",
  launch_failed: "Launch failed",
  launched: "Launched",
  not_queued: "Not queued",
  blocked: "Blocked",
  unknown: "Unknown",
};

const LAUNCH_STATUS_BADGES = {
  pending_launch: "badge badge-warning text-dark",
  launch_failed: "badge badge-danger text-white",
  launched: "badge badge-success text-white",
  not_queued: "badge badge-secondary text-white",
  blocked: "badge badge-danger text-white",
  unknown: "badge badge-light text-dark",
};

const SUMMARY_BOX = {
  ok: "plain-language-summary--ok",
  warning: "plain-language-summary--warning",
  danger: "plain-language-summary--danger",
};

export default {
  name: "TargetCampaignHealthContent",

  props: {
    health: {
      type: Object,
      required: true,
    },
    targetId: {
      type: [Number, String],
      default: null,
    },
    wrapperClass: {
      type: String,
      default: "",
    },
    /** When opened from /import-error, match the list magnifier tone/headline. */
    listTone: {
      type: String,
      default: "",
    },
    listHeadline: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      relaunching: false,
      relaunchMessage: "",
      relaunchMessageClass: "text-muted",
    };
  },

  computed: {
    canRelaunchSalesridge() {
      if (!this.targetId) {
        return false;
      }
      return shouldShowSalesridgeLaunchButton(
        this.health && this.health.salesridge_launch_ui
      );
    },
    relaunchButtonLabel() {
      return salesridgeLaunchButtonLabel(
        this.health && this.health.salesridge_launch_ui
      );
    },
    plainLanguage() {
      return (this.health && this.health.plain_language) || {};
    },
    displayTone() {
      const list = (this.listTone || "").trim();
      if (list === "ok" || list === "warning" || list === "danger") {
        return list;
      }
      return this.plainLanguage.tone || "warning";
    },
    displayHeadline() {
      const list = (this.listHeadline || "").trim();
      if (list) {
        return list;
      }
      return this.plainLanguage.headline || "";
    },
    summaryBoxClass() {
      return SUMMARY_BOX[this.displayTone] || SUMMARY_BOX.warning;
    },
    alerts() {
      return (this.health && this.health.alerts) || [];
    },
    contactFunnel() {
      return (this.health && this.health.contact_funnel) || {};
    },
    targetStats() {
      return this.health && this.health.target_stats;
    },
    coiToday() {
      return (this.health && this.health.coi_today) || { has_rows_today: false, campaigns: [] };
    },
    coiTomorrow() {
      return (
        (this.health && this.health.coi_tomorrow) || {
          has_rows_tomorrow: false,
          total_scheduled: 0,
          tomorrow_scheduled: 0,
          campaigns: [],
          by_date: [],
        }
      );
    },
    campaignPipelineRows() {
      const campaigns = (this.health && this.health.campaigns) || [];
      const coiTodayById = {};
      (this.coiToday.campaigns || []).forEach((coi) => {
        coiTodayById[coi.campaign_id] = coi;
      });
      const coiTomorrowById = {};
      (this.coiTomorrow.campaigns || []).forEach((row) => {
        coiTomorrowById[row.campaign_id] = row;
      });

      const byId = {};
      campaigns.forEach((c) => {
        byId[c.campaign_id] = { ...c };
      });
      Object.keys(coiTodayById).forEach((id) => {
        const campaignId = Number(id);
        if (!byId[campaignId]) {
          byId[campaignId] = { campaign_id: campaignId };
        }
      });
      Object.keys(coiTomorrowById).forEach((id) => {
        const campaignId = Number(id);
        if (!byId[campaignId]) {
          byId[campaignId] = { campaign_id: campaignId };
        }
      });

      return Object.keys(byId)
        .map(Number)
        .sort((a, b) => a - b)
        .map((campaignId) => {
          const c = byId[campaignId];
          const coi = coiTodayById[campaignId];
          const future = coiTomorrowById[campaignId];
          return {
            campaign_id: campaignId,
            campaign_name: c.campaign_name || "",
            campaign_status_id: c.campaign_status_id != null ? c.campaign_status_id : "—",
            contact_count: c.contact_count || 0,
            available_contacts: c.available_contacts || 0,
            csbc_waiting_today: c.csbc_waiting_today || 0,
            csbc_sent_today: c.csbc_sent_today || 0,
            activated_spark: c.activated_spark != null ? c.activated_spark : 0,
            demo_url_set: !!c.demo_url_set,
            has_coi_today: !!coi,
            quota_used: coi ? coi.quota_used : 0,
            quota_per_campaign: coi ? coi.quota_per_campaign : 0,
            quota_full: coi ? coi.quota_full : false,
            coi_status: coi ? coi.status : null,
            coi_current: coi ? coi.current : null,
            owarm_paused: coi ? coi.owarm_paused : false,
            scheduled_after_today: future ? future.scheduled_count : 0,
          };
        });
    },
    campaignPipelineTotals() {
      const rows = this.campaignPipelineRows;
      if (!rows.length) {
        return null;
      }
      return rows.reduce(
        (acc, row) => {
          acc.campaign_count += 1;
          acc.scheduled_after_today += row.scheduled_after_today;
          acc.csbc_waiting_today += row.csbc_waiting_today;
          acc.csbc_sent_today += row.csbc_sent_today;
          return acc;
        },
        {
          campaign_count: 0,
          scheduled_after_today: 0,
          csbc_waiting_today: 0,
          csbc_sent_today: 0,
        }
      );
    },
    campaignPipelineCaption() {
      const parts = [];
      if (!this.coiToday.has_rows_today) {
        parts.push("No COI planner record for today.");
      }
      parts.push(
        `Send queue today: ${this.sendQueue.today_total || 0}.`
      );
      if (this.sendQueue.waiting_to_enter_queue) {
        parts.push(
          `${this.sendQueue.waiting_to_enter_queue} still scheduled, not in queue yet.`
        );
      }
      const tomorrow = this.coiTomorrow;
      if (tomorrow.has_rows_tomorrow) {
        parts.push(
          `${tomorrow.total_scheduled} scheduled after today (csbc).`
        );
        const dates = tomorrow.by_date || [];
        if (dates.length === 1) {
          parts.push(`All on ${dates[0].scheduled_date_key}.`);
        } else if (dates.length > 1) {
          parts.push(
            dates
              .map((d) => `${d.scheduled_date_key}: ${d.scheduled_count}`)
              .join(" · ")
          );
        }
      } else {
        parts.push("Nothing scheduled after today yet.");
      }
      return parts.join(" ");
    },
    sendQueue() {
      return (
        (this.health && this.health.send_queue) || {
          today_total: 0,
          waiting_to_enter_queue: 0,
        }
      );
    },
    cbcQueue() {
      return (this.health && this.health.cbc_queue) || { pending_rows: 0 };
    },
    blockedReasons() {
      const launch = this.health && this.health.salesridge_launch;
      return (launch && launch.blocked_reasons) || [];
    },
    launchStatusLabel() {
      const status =
        this.health.salesridge_launch &&
        this.health.salesridge_launch.launch_status;
      return LAUNCH_STATUS_LABELS[status] || LAUNCH_STATUS_LABELS.unknown;
    },
    launchStatusBadgeClass() {
      const status =
        this.health.salesridge_launch &&
        this.health.salesridge_launch.launch_status;
      return LAUNCH_STATUS_BADGES[status] || LAUNCH_STATUS_BADGES.unknown;
    },
    salesridgeRemote() {
      const launch = this.health && this.health.salesridge_launch;
      return (launch && launch.remote) || null;
    },
    salesridgeRemoteAlertClass() {
      const status = this.salesridgeTaskStatus;
      if (status === "failed") return "alert-danger";
      if (status === "processing" || status === "pending" || status === "in_progress") {
        return "alert-warning";
      }
      return "alert-info";
    },
    salesridgeTaskStatus() {
      const batches = this.salesridgeRemote && this.salesridgeRemote.export_batches;
      return (batches && batches.taskStatus) || "";
    },
    salesridgeTaskSource() {
      const batches = this.salesridgeRemote && this.salesridgeRemote.export_batches;
      return (batches && batches.taskSource) || "";
    },
    salesridgeTaskMessage() {
      const batches = this.salesridgeRemote && this.salesridgeRemote.export_batches;
      return (batches && batches.message) || "";
    },
    salesridgeResponse() {
      return (this.health && this.health.salesridge_response) || {};
    },
    showSalesridgeResponseBox() {
      const r = this.salesridgeResponse;
      if (!r || typeof r !== "object") {
        return false;
      }
      return !!(
        r.salesridge_id
        || r.has_live_order_stats
        || r.launch_error
        || (r.live_fetch_errors && r.live_fetch_errors.length)
        || this.hasStoredLeadsTotal
      );
    },
    hasStoredLeadsTotal() {
      const v = this.salesridgeResponse.stored_leads_total;
      return v !== null && v !== undefined && v !== "";
    },
    salesridgeLiveFetchErrors() {
      const errs = this.salesridgeResponse.live_fetch_errors;
      return Array.isArray(errs) ? errs : [];
    },
    salesridgeExportTaskStatus() {
      const task = this.salesridgeResponse.live_export_task;
      if (!task || !task.task_status) {
        return "";
      }
      let label = String(task.task_status);
      if (task.task_source) {
        label += " (" + task.task_source + ")";
      }
      return label;
    },
    salesridgeExportTaskMessage() {
      const task = this.salesridgeResponse.live_export_task;
      return (task && task.message) || "";
    },
    salesridgeBatchStatusSummary() {
      const task = this.salesridgeResponse.live_export_task;
      const counts = task && task.batch_status_counts;
      if (!counts || typeof counts !== "object") {
        return "";
      }
      const parts = Object.keys(counts).map(
        (status) => status + ": " + counts[status]
      );
      return parts.length ? "Batch statuses — " + parts.join(", ") : "";
    },
    salesridgeLaunchErrorMessage() {
      const err = this.salesridgeResponse.launch_error;
      if (!err) {
        return "";
      }
      const code = err.error_code ? "[" + err.error_code + "] " : "";
      return code + (err.error_message || "");
    },
    salesridgeRawJson() {
      const r = this.salesridgeResponse;
      const payload = {
        stored_leads_total: r.stored_leads_total,
        stored_apollo_total: r.stored_apollo_total,
        live_order_stats: r.live_order_stats,
        live_export_task: r.live_export_task,
        launch_error: r.launch_error,
      };
      const hasData = payload.live_order_stats
        || payload.launch_error
        || payload.stored_leads_total != null;
      if (!hasData) {
        return "";
      }
      try {
        return JSON.stringify(payload, null, 2);
      } catch (e) {
        return "";
      }
    },
  },

  methods: {
    relaunchSalesridge() {
      if (!this.targetId || this.relaunching) {
        return;
      }
      const launch = this.health && this.health.salesridge_launch;
      const previousId = launch && launch.salesridge_id;
      const confirmed = window.confirm(
        (this.relaunchButtonLabel || "Relaunch SalesRidge search") + "?\n\n"
          + "This only launches the search on SalesRidge (leadsSearch). "
          + "Import runs on the normal cron schedule"
          + (previousId ? " (replaces " + previousId + ")" : "")
          + ". Continue?"
      );
      if (!confirmed) {
        return;
      }

      this.relaunching = true;
      this.relaunchMessage = "";
      targetsService
        .relaunchSalesridgeSearch(this.targetId)
        .then((res) => {
          this.relaunchMessage =
            (res && res.detail) || "SalesRidge search launched — refresh to see the new order id.";
          this.relaunchMessageClass = "text-success";
          this.$emit("relaunched", res);
        })
        .catch((err) => {
          this.relaunchMessage = formatSalesridgeRelaunchError(this.$i18n, err);
          this.relaunchMessageClass = "text-danger";
        })
        .finally(() => {
          this.relaunching = false;
        });
    },
    salesridgeOrderStat(key) {
      const stats = this.salesridgeRemote && this.salesridgeRemote.order_stats;
      if (!stats || stats[key] === undefined || stats[key] === null) {
        return 0;
      }
      return stats[key];
    },
    salesridgeLiveStat(key) {
      const stats = this.salesridgeResponse.live_order_stats;
      if (!stats || stats[key] === undefined || stats[key] === null) {
        return 0;
      }
      return stats[key];
    },
    stepStatusIcon(status) {
      if (status === "ok") return "✓";
      if (status === "pending") return "⏳";
      if (status === "blocked") return "✗";
      return "○";
    },
    stepStatusClass(status) {
      if (status === "ok") return "text-success";
      if (status === "pending") return "text-warning";
      if (status === "blocked") return "text-danger";
      return "text-muted";
    },
    alertClass(tone) {
      if (tone === "danger") return "alert alert-danger py-2 mb-1";
      if (tone === "warning") return "alert alert-warning py-2 mb-1";
      if (tone === "info") return "alert alert-info py-2 mb-1";
      if (tone === "ok") return "alert alert-success py-2 mb-1";
      return "alert alert-secondary py-2 mb-1";
    },
    conditionIcon(c) {
      const tone = c.tone || (c.met ? "ok" : "fail");
      if (tone === "pending") return "⏳";
      if (tone === "fail") return "✗";
      return "✓";
    },
    conditionIconClass(c) {
      const tone = c.tone || (c.met ? "ok" : "fail");
      if (tone === "pending") return "text-warning";
      if (tone === "fail") return "text-danger";
      return "text-success";
    },
  },

  filters: {
    numberFormat(value) {
      const n = parseInt(value, 10);
      if (isNaN(n)) return value;
      return n.toLocaleString();
    },
  },
};
</script>

<style scoped>
.plain-language-summary--ok {
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
}
.plain-language-summary--warning {
  background: #fff8e1;
  border: 1px solid #ffe082;
}
.plain-language-summary--danger {
  background: #ffebee;
  border: 1px solid #ef9a9a;
}
.pipeline-steps {
  list-style: none;
  padding-left: 0;
}
.pipeline-steps li {
  list-style: none;
}
.salesridge-raw-json {
  max-height: 14rem;
  overflow: auto;
  font-size: 0.7rem;
  white-space: pre-wrap;
  word-break: break-word;
}
.technical-details summary {
  user-select: none;
}
.technical-details[open] summary {
  margin-bottom: 0.75rem;
}
</style>
